import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { CallButton } from "@/components/common/CallButton";
import { AddFollowUpDialog } from "@/components/leads/AddFollowUpDialog";
import { ChangeStatusDialog } from "@/components/leads/ChangeStatusDialog";
import { DealProcessingPanel } from "@/components/leads/DealProcessingPanel";
import { FollowUpTimeline } from "@/components/leads/FollowUpTimeline";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { STATUS_ORDER, statusMeta } from "@/lib/constants";
import { formatINR, formatDate, formatDateShort, timeAgo, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  ArrowLeft, Store, User, Pencil, Plus, RefreshCw, CalendarClock, Car,
  ChevronRight, XCircle, CheckCircle2, MapPin, Phone,
} from "lucide-react";

const PIPELINE = ["NEW", "CONTACTED", "FOLLOW_UP", "NEGOTIATION", "OFFER", "DEAL_DONE"];

function PipelineStepper({ status }) {
  if (status === "LOST") {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-destructive/10 p-3 ring-1 ring-inset ring-destructive/20">
        <XCircle className="h-5 w-5 text-destructive" />
        <span className="text-sm font-semibold text-destructive">Lead marked Lost</span>
      </div>
    );
  }
  const idx = PIPELINE.indexOf(status);
  return (
    <div className="no-scrollbar flex items-center gap-1 overflow-x-auto rounded-xl border border-border bg-card p-3 shadow-card">
      {PIPELINE.map((s, i) => {
        const meta = statusMeta(s);
        const done = i <= idx;
        return (
          <React.Fragment key={s}>
            <div className="flex shrink-0 flex-col items-center gap-1">
              <span className={cn("flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold",
                done ? cn(meta.solid) : "bg-secondary text-muted-foreground")}>
                {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={cn("whitespace-nowrap text-[9px] font-medium", done ? "text-foreground" : "text-muted-foreground")}>{meta.label}</span>
            </div>
            {i < PIPELINE.length - 1 && <span className={cn("h-0.5 w-4 shrink-0 rounded-full", i < idx ? "bg-brand" : "bg-border")} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/60 py-2 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground text-right">{value}</span>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [fuOpen, setFuOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/leads/${id}`)
      .then((res) => setData(res.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Could not load lead");
        navigate("/leads", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader label="Loading lead…" />;
  if (!data) return null;

  const { lead, dealership, follow_ups } = data;
  const isIndividual = lead.lead_type === "individual";
  const contactNumber = isIndividual ? lead.customer_mobile : dealership?.mobile;
  const title = [lead.make, lead.model].filter(Boolean).join(" ") || lead.vehicle_number || lead.customer_name || lead.lead_no;
  const hasProcessing = lead.status === "DEAL_DONE" || lead.deal_processing;

  const onLeadUpdated = (updated) => {
    setData((d) => ({ ...d, lead: { ...d.lead, ...updated } }));
  };

  const fuTone = (() => {
    if (!lead.next_follow_up || ["DEAL_DONE", "LOST"].includes(lead.status)) return null;
    const t = todayISO();
    if (lead.next_follow_up < t) return { label: "Overdue", cls: "text-destructive" };
    if (lead.next_follow_up === t) return { label: "Due today", cls: "text-warning" };
    return { label: formatDateShort(`${lead.next_follow_up}T00:00:00`), cls: "text-brand" };
  })();

  return (
    <div className="animate-fade-in-up space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-semibold text-muted-foreground">{lead.lead_no}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-secondary-foreground">
              {isIndividual ? <User className="h-3 w-3" /> : <Store className="h-3 w-3" />}
              {isIndividual ? "Individual" : "Dealer"}
            </span>
          </div>
          <h1 className="truncate text-xl font-bold text-foreground sm:text-2xl">{title}</h1>
        </div>
        <Button variant="outline" size="icon" onClick={() => navigate(`/leads/${id}/edit`)}><Pencil className="h-4 w-4" /></Button>
      </div>

      <div className="flex items-center gap-2">
        <StatusBadge status={lead.status} />
        {lead.owner_name && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><User className="h-3 w-3" /> {lead.owner_name}</span>}
        <span className="ml-auto text-[11px] text-muted-foreground">Updated {timeAgo(lead.updated_at)}</span>
      </div>

      <PipelineStepper status={lead.status} />

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {contactNumber ? (
          <CallButton number={contactNumber} full />
        ) : (
          <Button variant="outline" disabled className="w-full"><Phone className="h-4 w-4" /> No number</Button>
        )}
        <Button variant="brand" className="w-full" onClick={() => setFuOpen(true)}><Plus className="h-4 w-4" /> Follow-up</Button>
        <Button variant="outline" className="w-full" onClick={() => setStatusOpen(true)}><RefreshCw className="h-4 w-4" /> Status</Button>
        <Button variant="outline" className="w-full" onClick={() => navigate(`/leads/${id}/edit`)}><Pencil className="h-4 w-4" /> Edit</Button>
      </div>

      {lead.status === "LOST" && lead.lost_reason && (
        <div className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive ring-1 ring-inset ring-destructive/20">
          <b>Lost reason:</b> {lead.lost_reason}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="followups">Follow-ups {follow_ups?.length ? `(${follow_ups.length})` : ""}</TabsTrigger>
          <TabsTrigger value="deal">Deal</TabsTrigger>
        </TabsList>

        {/* DETAILS */}
        <TabsContent value="details" className="space-y-4 pt-4">
          {/* Pricing */}
          {(lead.demand != null || lead.offer_price != null) && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                <div className="text-xs text-muted-foreground">Demand</div>
                <div className="tnum mt-1 font-display text-xl font-extrabold text-foreground">{formatINR(lead.demand)}</div>
              </div>
              <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                <div className="text-xs text-muted-foreground">Our Offer</div>
                <div className="tnum mt-1 font-display text-xl font-extrabold text-brand">{formatINR(lead.offer_price)}</div>
              </div>
            </div>
          )}

          {/* Vehicle */}
          <div className="rounded-xl border border-border bg-card p-4 shadow-card">
            <h3 className="mb-1 flex items-center gap-2 text-sm font-bold text-foreground"><Car className="h-4 w-4 text-brand" /> Vehicle</h3>
            <InfoRow label="Vehicle Number" value={lead.vehicle_number} />
            <InfoRow label="Make" value={lead.make} />
            <InfoRow label="Model" value={lead.model} />
            <InfoRow label="Variant" value={lead.variant} />
            <InfoRow label="Year" value={lead.year} />
            <InfoRow label="Fuel" value={lead.fuel_type} />
            {isIndividual && <InfoRow label="KM Driven" value={lead.km} />}
            <InfoRow label="Alloy" value={lead.alloy} />
            <InfoRow label="Condition" value={lead.vehicle_condition} />
            {lead.remarks && <InfoRow label="Remarks" value={lead.remarks} />}
          </div>

          {/* Party */}
          <div className="rounded-xl border border-border bg-card p-4 shadow-card">
            <h3 className="mb-1 flex items-center gap-2 text-sm font-bold text-foreground">
              {isIndividual ? <User className="h-4 w-4 text-brand" /> : <Store className="h-4 w-4 text-brand" />}
              {isIndividual ? "Customer" : "Dealership"}
            </h3>
            {isIndividual ? (
              <>
                <InfoRow label="Name" value={lead.customer_name} />
                <InfoRow label="Mobile" value={lead.customer_mobile} />
                <InfoRow label="Alternate" value={lead.customer_alt_mobile} />
                <InfoRow label="City" value={lead.customer_city} />
                <InfoRow label="Address" value={lead.customer_address} />
              </>
            ) : dealership ? (
              <button onClick={() => navigate(`/dealerships/${dealership.id}`)} className="flex w-full items-center gap-3 rounded-lg py-1 text-left">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground"><Store className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground">{dealership.name}</div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {dealership.contact_person && <span>{dealership.contact_person}</span>}
                    {dealership.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {dealership.city}</span>}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ) : (
              <p className="py-2 text-sm text-muted-foreground">No dealership linked.</p>
            )}
          </div>

          {/* Meta */}
          <div className="rounded-xl border border-border bg-card p-4 shadow-card">
            <InfoRow label="Created by" value={lead.owner_name} />
            <InfoRow label="Created on" value={formatDate(lead.created_at)} />
            <InfoRow label="Last activity" value={formatDate(lead.last_activity)} />
          </div>
        </TabsContent>

        {/* FOLLOW-UPS */}
        <TabsContent value="followups" className="space-y-4 pt-4">
          {fuTone && (
            <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-3 shadow-card">
              <CalendarClock className={cn("h-5 w-5", fuTone.cls)} />
              <span className="text-sm text-foreground">Next follow-up: <b className={fuTone.cls}>{fuTone.label}</b></span>
              <Button variant="brand" size="sm" className="ml-auto" onClick={() => setFuOpen(true)}><Plus className="h-4 w-4" /> Add</Button>
            </div>
          )}
          {!fuTone && (
            <Button variant="brand" className="w-full" onClick={() => setFuOpen(true)}><Plus className="h-4 w-4" /> Add Follow-up</Button>
          )}
          <FollowUpTimeline followUps={follow_ups} />
        </TabsContent>

        {/* DEAL */}
        <TabsContent value="deal" className="pt-4">
          {hasProcessing ? (
            <DealProcessingPanel lead={lead} onUpdated={onLeadUpdated} />
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-card/60 p-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-muted-foreground"><CheckCircle2 className="h-6 w-6" /></div>
              <h3 className="text-base font-semibold text-foreground">Not a deal yet</h3>
              <p className="mx-auto mt-1 max-w-xs text-sm text-muted-foreground">
                Deal processing (Documents → Approval → Payment → COD) unlocks once this lead is marked Deal Done.
              </p>
              <Button variant="success" className="mt-4" onClick={() => setStatusOpen(true)}>
                <CheckCircle2 className="h-4 w-4" /> Mark Deal Done
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AddFollowUpDialog open={fuOpen} onOpenChange={setFuOpen} leadId={id} onAdded={load} />
      <ChangeStatusDialog open={statusOpen} onOpenChange={setStatusOpen} lead={lead}
        onUpdated={(u) => { onLeadUpdated(u); if (u.status === "DEAL_DONE") setTab("deal"); load(); }} />
    </div>
  );
}
